import React, {useCallback, useEffect, useState} from 'react';
import _ from 'lodash';
import {createSearchParams, useOutletContext, useParams, useSearchParams} from 'react-router-dom';

import {connect, resourceActions} from 'redux/index.js';
import K from 'k';

import DirectionBoardView from './direction-board-view';
import GalleryBoard from './gallery-board';
import {Button, Dropdown, Icon} from '@henrybuilt/react-lib';
import {loadTagsData} from 'helpers/loaders';

export const ModeOptions = {
  RORSCHACHSELECTION: 'rorschach_image_selection',
  PROPOSALSELECTION: 'proposal_image_selection',
  RORSCHACH: 'rorschach_test',
  DIRECTION: 'direction_board',
  PROPOSAL: 'proposal_imagery'
};

export const RorschachTestDataSections = {
  ORGANIZE: 'toOrganize',
  OVERVIEW: 'overview',
  THEMES: 'importantThemes',
  DETAILS: 'specificDetails',
  PROPOSALCOVERPAGE: 'proposalCoverPage',
  PROPOSALIMAGEGALLERY: 'proposalImageGallery'
};

export const defaultRorschachTestData = {
  toOrganize: [],
  overview: [],
  importantThemes: [],
  specificDetails: [],
  proposalCoverPage: [],
  proposalImageGallery: []
};

const RorschachPage = ({
  // redux store
  trackProjects, trackTags, trackTagCategories, trackMedia,
  updateProject,
}) => {
  const [project, isLoading, setHeaderItems] = useOutletContext();
  const params = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const [isHudVisible, setIsHudVisible] = useState(ModeOptions.RORSCHACH);
  const [notesAreVisible, setNotesAreVisible] = useState(true);
  const [imageSizeKey, setImageSizeKey] = useState('small'); //HINT 'small' or 'large'

  const mode = searchParams.get('mode');
  const modeOptions = _.map(_.values(ModeOptions), value => ({label: value.split('_').join(' '), value}));

  const styles = {
    controls: {
      position: 'relative',
      display: 'flex',
      alignItems: 'center',
      gap: K.spacing,
    },
    control: {
      display: 'flex',
      flexDirection: 'row',
      justifyContent: 'start',
      alignItems: 'center',
      gap: K.spacing / 2,
      visibility: isHudVisible ? 'visible' : 'hidden'
    },
    button: {
      backgroundColor: 'transparent',
      justifyContent: 'center',
      alignItems: 'center',
      cursor: 'pointer',
    },
    iconButton: {
      width: 33,
      height: 33,
    },
    textButton: {

    },
    textButtonContent: {
      margin: `0 ${K.spacing}px 0 ${K.spacing}px`,
      textTransform: 'uppercase',
      color: 'white'
    },
    dropdown: {
      marginRight: K.spacing,
      visibility: isHudVisible ? 'visible' : 'hidden',
    },
  };

  useEffect(() => {
    fetchTagsData(params);
  }, [params]);

  //HINT sets mode to 'index' by default if the URLSearchParams mode is currently null or undefined
  useEffect(() => {
    if (mode == undefined) handleModeChange(ModeOptions.RORSCHACHSELECTION);
    // else if (mode === ModeOptions.RORSCHACH) setIsHudVisible(false);
  }, [mode]);

  //HINT initialize PageHeader items
  useEffect(() => {
    generateHeaderItems();
  }, [project, isHudVisible, notesAreVisible, imageSizeKey]);

  useEffect(() => {
    if (project?.id && _.isEmpty(project.rorschachTestData)) {
      initializeProjectRorschachTestData(project.id);
    }
  }, [project]);

  const fetchTagsData = useCallback(async (params) => {
    const resourceActions = {trackTags, trackTagCategories};

    await loadTagsData({resourceActions, params});
  });

  //HINT takes in a new mode: string and updates URLSearchParams to match. See ModeOptions above.
  const handleModeChange = (mode) => {
    setSearchParams(
      createSearchParams({mode})
    );
  };

  const toggleIsModeVisible = () => setIsHudVisible(!isHudVisible);

  const handleImageSizeChange = (sizeKey) => () => {
    setImageSizeKey(sizeKey);
  };

  const handleToggleNotesVisibility = () => {
    setNotesAreVisible(!notesAreVisible);
  };

  const handlePrintClick = () => {
    print();
  };

  const getProjectTitle = () => {
    const companyName = project?.companyKey === 'vp' ? 'Space Theory' : 'Henrybuilt';

    return `${companyName} for ${project?.clientName}`;
  };

  const getFileTitle = () => {
    return 'Direction Board - ' + getProjectTitle();
  };

  const print = () => {
    var cachedTitle = document.title;

    document.title = getFileTitle();

    setTimeout(() => {
      window.print();

      document.title = cachedTitle;
    });
  };

  const generateHeaderItems = () => {
    if (setHeaderItems) {
      setHeaderItems([
        <div key={'project-title'} style={{marginLeft: '1rem'}}>{getProjectTitle()}</div>,
        <div key='hud-controls' style={styles.controls}>
          <Dropdown
            label={'Mode'}
            collapseOnChange={true}
            style={styles.dropdown}
            options={modeOptions}
            onChange={handleModeChange}
            value={mode}
          />
          {_.includes([ModeOptions.DIRECTION, ModeOptions.PROPOSAL], mode) && (
            <>
              <span key={'print'} style={styles.control}>
                <Button style={{...styles.button, ...styles.iconButton}} onClick={handlePrintClick} icon={<Icon theme='white' name='print'/>} />
              </span>
              <span key={'toggle-notes'} style={styles.control}>
                <Button style={{...styles.button, ...styles.iconButton}} onClick={handleToggleNotesVisibility} icon={<Icon style={styles.radioIcon} theme='white' name={notesAreVisible ? 'notesVisible' : 'notesHidden'} />} />
              </span>
              <span key={'image-size-radio'} style={styles.control}>
                <Button style={{...styles.button, ...styles.iconButton}} onClick={handleImageSizeChange('small')} icon={<Icon style={styles.radioIcon} theme='white' name={imageSizeKey !== 'small' ? 'smallIcon' : 'smallIconActive'} />} />
                <Button style={{...styles.button, ...styles.iconButton}} onClick={handleImageSizeChange('large')} icon={<Icon style={styles.radioIcon} theme='white' name={imageSizeKey !== 'large' ? 'largeIcon' : 'largeIconActive'} />} />
              </span>
            </>
          )}
          <Button style={{...styles.button, ...styles.iconButton}} onClick={toggleIsModeVisible} icon={<Icon style={styles.radioIcon} theme='white' name={isHudVisible ? 'privacyEyeOpen' : 'privacyEyeClosed'}/>} />
        </div>,
      ]);
    }
  };

  const initializeProjectRorschachTestData = useCallback(async (id) => {
    await updateProject({id, props: {rorschachTestData: defaultRorschachTestData}});
  });

  let currentView = <GalleryBoard {...{mode, isHudVisible}} />;

  if (_.includes([ModeOptions.DIRECTION, ModeOptions.PROPOSAL], mode)) {
    currentView = <DirectionBoardView {...{mode, isHudVisible, notesAreVisible, imageSizeKey}}/>;
  }

  return currentView;
};

export default connect({
  mapState: state => {
    return {
      projects: state.resources.projects.byId,
    };
  },
  mapDispatch: {
    ..._.pick(resourceActions.media, ['trackMedia']),
    ..._.pick(resourceActions.projects, ['trackProjects', 'updateProject']),
    ..._.pick(resourceActions.tagCategories, ['trackTagCategories']),
    ..._.pick(resourceActions.tags, ['trackTags']),
  }
})(RorschachPage);
